'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { smoothScroll } from '../../utils/animation/smoothScroll';

const Stream = dynamic(() => import('@cloudflare/stream-react').then(m => m.Stream), {
  ssr: false,
});
const StreamModal = dynamic(() => import('./StreamModal'), { ssr: false });

interface HeroProps {
  title: string;
  subtitle: string;
  buttonText: string;
  buttonHref: string;
  rightTitle: string;
  rightDescription: string;
  backgroundImage: string;
  background?: string;
  titleColor?: string;
  videoId?: string;
  customerCode?: string;
  videoButtonText?: string;
  videoButtonColorClass?: string;
  inlineVideo?: boolean;
  videoPosterUrl?: string;
}

const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  buttonText,
  buttonHref,
  rightTitle,
  rightDescription,
  backgroundImage,
  background = 'bg-white',
  titleColor = 'text-primary',
  videoId,
  customerCode,
  videoButtonText = 'Watch our explainer video to learn more',
  videoButtonColorClass = 'text-primary',
  inlineVideo = false,
  videoPosterUrl,
}) => {
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const videoModalId = !inlineVideo ? videoId : undefined;
  const shouldUseVideoModal = Boolean(videoModalId);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      e.preventDefault();
      const isAnchor = buttonHref.startsWith('#');

      if (isAnchor) {
        smoothScroll(buttonHref);
      } else {
        router.push(buttonHref);
      }
    };

    const button = document.querySelector<HTMLAnchorElement>('a[href="' + buttonHref + '"]');
    if (button) {
      button.addEventListener('click', handleClick as EventListener);
    }

    return () => {
      if (button) {
        button.removeEventListener('click', handleClick as EventListener);
      }
    };
  }, [buttonHref, router]);

  const handlePlayClick = () => {
    setShowOverlay(false);
  };

  return (
    <div className={`rh-hero relative overflow-hidden ${background}`} id="hero">
      <Image
        src={backgroundImage}
        alt=""
        aria-hidden="true"
        fill
        priority
        sizes="100vw"
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
      />
      <div className="relative z-10 container mx-auto px-4 pt-32 sm:pt-40 xl:pt-56 pb-12 sm:pb-24 sm:px-6 lg:px-8 flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-8 items-center">
          <div className="max-w-full xl:pr-2">
            <h1
              className={`${titleColor} mb-2 rh-rise`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <p
              className="text-lg sm:text-xl mb-12 rh-rise rh-rise-2"
              dangerouslySetInnerHTML={{ __html: subtitle }}
            ></p>
            {buttonText && buttonHref && (
              <a href={buttonHref} className="rh-btn-black-lg rh-rise rh-rise-3 inline-block">
                {buttonText}
              </a>
            )}
          </div>
          <div
            className={`flex flex-col ${rightTitle || rightDescription ? 'justify-end' : 'justify-center'} h-full rh-rise rh-rise-4`}
          >
            <div className={rightTitle || rightDescription ? 'mt-auto' : ''}>
              {rightTitle && <h2>{rightTitle}</h2>}
              {rightDescription && <p className="mb-2">{rightDescription}</p>}
              {videoId && inlineVideo ? (
                <div className={rightTitle || rightDescription ? 'mt-4' : ''}>
                  <div
                    className="relative rounded-2xl overflow-hidden bg-primary group"
                    style={{ aspectRatio: '16 / 9' }}
                  >
                    <div className="absolute inset-0">
                      <Stream
                        src={videoId}
                        customerCode={customerCode}
                        controls
                        responsive
                        preload="metadata"
                        width="100%"
                        height="100%"
                      />
                    </div>

                    {/* Custom poster overlay that hides when clicked */}
                    {showOverlay && videoPosterUrl && (
                      <>
                        {/* Poster Image */}
                        <div
                          className="absolute inset-0 bg-cover bg-center z-10"
                          style={{ backgroundImage: `url('${videoPosterUrl}')` }}
                        />

                        {/* Dark Overlay */}
                        <div className="absolute inset-0 bg-black/10 z-20" />

                        {/* Play Button */}
                        <button
                          onClick={handlePlayClick}
                          className="absolute inset-0 flex items-center justify-center group hover:bg-black/20 transition-all duration-300 motion-reduce:transition-none z-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white"
                          aria-label="Play video"
                        >
                          <div className="flex items-center justify-center w-20 h-20 bg-white rounded-full group-hover:scale-110 transition-all duration-300 motion-reduce:transition-none shadow-2xl">
                            <svg
                              className="w-8 h-8 text-primary ml-1"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                shouldUseVideoModal && (
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className={`inline-flex items-center gap-2 ${videoButtonColorClass} text-sm sm:text-lg md:text-xl lg:text-[1.375rem] font-selawik font-semibold rounded-full px-2 py-1 sm:px-3 border border-primary/10 bg-primary/5 hover:bg-primary/10 hover:border-primary/20 transition-colors shadow-sm hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-7 h-7 mt-0.5 flex-shrink-0"
                    >
                      <rect
                        x="2"
                        y="4"
                        width="20"
                        height="16"
                        rx="3"
                        ry="3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                    {videoButtonText}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Only mount the modal when opened, to keep the Cloudflare Stream chunk off the initial path */}
      {videoModalId && isModalOpen && (
        <StreamModal
          videoId={videoModalId}
          customerCode={customerCode}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default Hero;
